import React, { useEffect, useState } from 'react';

import { Button, Col, Divider, Modal, Radio, Row, notification } from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/es-mx';
import 'dayjs/locale/en';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

import { glLettersLogo } from 'src/assets/images';
import { Input } from 'src/components';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { AppI, CartI, CartProductI, CommonI, deleteProdCart, setDiscountCode, setProdCart, setUserInfo, store } from 'src/redux';

import styles from './ShoppingInfo.module.css';
import CheckoutForm from './CheckoutForm';
import { getProductById, validatePromoCode } from 'src/api';
import ShoppingForm from './ShoppingForm';
import { formatter, getAmountByMonth, getAmountDiscount, getPromo, getSellPriceByLocale, getTotalDeferred, LocaleT } from 'src/utils';

import bagI18n from 'src/i18n/bag.i18n.json';
import notifI18n from 'src/i18n/notifications.i18n.json';
import { getWallets } from 'src/api/wallets';

const KEY = process.env.REACT_APP_EPIRTS_KEY;
// Publishable key stripe
const stripePromise = loadStripe(KEY as string);

export interface SaleInfoI {
   date: string;
   payment_type: 'card';
   promo_code?: string;
   wallet?: string;
   items: Array<{
      product_id: number;
      quantity: number;
      price: number;
      item_name: string;
   }>;
}

const AMOUNT_MINIMAL_MX = 800;
const AMOUNT_MINIMAL_EU = 45;
const AMOUNT_MINIMAL_LATUSD = 50; // ARG / PAN / P. Rico / C. Rica / Perú / Chile
const AMOUNT_MINIMAL_ES = 40;
const AMOUNT_MINIMAL_CO = 2000;

// Cargo de travel $45 USA
// Cargo de travel $100,000 COL
// España de travel $25 España

const getMinimalAmountByLocale = (locale: LocaleT) => {
   switch (locale) {
      case 'en':
         return AMOUNT_MINIMAL_EU;
      case 'es-ES':
      case 'fr':
      case 'it':
         return AMOUNT_MINIMAL_ES;
      case 'co':
         return AMOUNT_MINIMAL_CO;
      case 'ar':
      case 'pa':
      case 'pr':
      case 'cr':
      case 'pe':
      case 'cl':
      case 'es-UY':
      case 'es-PY':
         return AMOUNT_MINIMAL_LATUSD;
      default:
         return AMOUNT_MINIMAL_MX;
   }
};

const ShoppingInfo = () => {
   const { total, products, discountCode, user } = useAppSelector(({ cart }: { cart: CartI }) => cart.data);
   const { lang, locale } = useAppSelector(({ app }: { app: AppI }) => app);
   const { currentZone } = useAppSelector(({ common }: { common: CommonI }) => common);
   const [gift, setGift] = useState<any>({});
   const dispatch = useAppDispatch();
   const supportedLocales = ['es', 'en', 'es-ES', 'co', 'ar', 'pa', 'cl', 'pe', 'pr', 'fr', 'it', 'es-UY', 'es-PY'] as const;
   const minimalAmount = getMinimalAmountByLocale(supportedLocales.includes(locale as any) ? (locale as LocaleT) : 'es');

   dayjs.locale(lang === 'es' ? 'es-mx' : 'en');

   const [showModal, setShowModal] = useState(false);
   const [payForm, setPayForm] = useState(1);
   const [width, setWidth] = useState(window.innerWidth);
   const [code, setCode] = useState('');
   const [discount, setDiscount] = useState<any>({
      amount: 0,
      percentage: false,
   });

   const TRAVEL_EXPENSES =
      locale === 'en'
         ? 45
         : locale === 'co'
           ? 100000
           : locale === 'es-ES' || locale === 'fr' || locale === 'it'
             ? 25
             : currentZone.travelers
               ? Number(currentZone.travelers)
               : 0;

   const [wallet, setWallet] = useState<any>('');
   const [walletType, setWalletType] = useState<any>('');

   const [sale, setSale] = useState<SaleInfoI>({
      date: '',
      payment_type: 'card',
      promo_code: discountCode,
      wallet: wallet,
      items: [],
   });

   const handleCheckout = async () => {
      try {
         const body: SaleInfoI = {
            date: `${dayjs(user.date).format('YYYY-MM-DD')} ${user.hour}`,
            payment_type: 'card',
            promo_code: discountCode,
            wallet: wallet.code,
            items: products.map((i: any) => ({
               product_id: Number(i.id),
               quantity: i.numberProducts,
               price: Number(i.cost),
               item_name: i.nameProduct[locale],
            })),
         };
         setSale(body);
         setShowModal(true);
      } catch (error) {
         console.log(error);
      }
   };

   const handleValidateCode = async () => {
      if (code === wallet) {
         return;
      }
      setDiscount({});
      setGift({});
      try {
         const wallet = await getWallets(code);
         if (wallet.data === null) {
            const { data, success } = await validatePromoCode(code);
            if (success === 'true') {
               if (user.date === '') {
                  setDiscount(undefined);
                  dispatch(setDiscountCode(''));
                  notification.warning({
                     message: notifI18n[lang].dateCodeInvalidTitleNotif,
                     description: notifI18n[lang].dateCodeInvalidDescriptionNotif,
                  });
               } else {
                  const currentDate = dayjs(user.date).format('YYYY/MM/DD');
                  const startDate = dayjs(data.starts_at).format('YYYY/MM/DD');
                  const expireDate = dayjs(data.expires_at).format('YYYY/MM/DD');
                  if (
                     user.date &&
                     (dayjs(currentDate).isSame(startDate) || dayjs(currentDate).isAfter(startDate)) &&
                     (dayjs(currentDate).isSame(expireDate) || dayjs(currentDate).isBefore(expireDate))
                  ) {
                     setDiscount({
                        amount: data.discount,
                        percentage: true,
                     });
                     dispatch(setDiscountCode(code));
                  } else {
                     setDiscount(undefined);
                     dispatch(setDiscountCode(''));
                     notification.warning({
                        message: notifI18n[lang].codeTitleNotif,
                        description: notifI18n[lang].codeDescriptionNotif,
                     });
                  }
               }
            } else {
               notification.warning({
                  message: notifI18n[lang].codeTitleNotif,
                  description: notifI18n[lang].codeDescriptionNotif,
               });
            }
         } else {
            const { data } = wallet;
            setWallet(data.code);
            setWalletType(data.type);
            dispatch(setDiscountCode(data.code));
            //iswallet
            //Validar zona con la de el formulario
            //Verificar si el usuario esta en una zona valida, si no, rechazarlo "Codigo invalido"
            // if((promo.zone_id !== null)){
            //    console.log('promo.zone_id : ', promo.zone_id );
            //    console.log('currentZone.id: ', currentZone.id);
            //    console.log('user: ', user);
            //    if( promo.zone_id !==  currentZone.id){
            //       setDiscount(undefined);
            //       dispatch(setDiscountCode(''));
            //       notification.warning({
            //          message: notifI18n[lang].dateCodeInvalidTitleNotif,
            //          description: notifI18n[lang].invalidCity,
            //       });
            //    }

            // }
            //Verificar si tiene product id entonces esta asociada a un producto
            if (data.product_id) {
               const response = await getProductById(data.product_id);

               if (response.success) {
                  if (products.filter((p: any) => p.id === response.data.id).length === 0) {
                     dispatch(
                        setProdCart({
                           id: response.data.id,
                           nameProduct: {
                              en: response.data.name,
                              es: response.data.name,
                           },
                           numberProducts: 1,
                           cost: response.data[getSellPriceByLocale(locale)],
                           uri: response.data.photo,
                           gift: true,
                        })
                     );
                  }

                  setGift(response.data);
               }

               dispatch(setDiscountCode(null));
            } else {
               setDiscount({
                  amount: data.amount,
                  percentage: false,
               });
               dispatch(setDiscountCode(null));
            }
         }
      } catch (error: any) {
         dispatch(setDiscountCode(''));
         setDiscount(undefined);
         notification.error({
            message: 'Error',
            description: `${notifI18n[lang].commonDescriptionError} ${error.message}`,
         });
      }
   };

   const handlePays = (monthNumber: number) => {
      setPayForm(monthNumber);
   };

   useEffect(() => {
      window.addEventListener('resize', () => {
         setWidth(window.innerWidth);
      });
      return () => {
         window.removeEventListener('resize', () => {});
      };
   }, []);

   useEffect(() => {
      if (currentZone.locality) {
         dispatch(
            setUserInfo({
               ...user,
               address: currentZone.locality,
            })
         );
      }
   }, []);

   useEffect(() => {
      setPayForm(1);
   }, [discount]);

   return (
      <div className={styles.cart_detail}>
         <div className={styles.absolute_div} />
         <div className={`container pt-5 text-center ${styles.relative_div}`}>
            <h2>{bagI18n[lang].yourBagText}</h2>
            <Row style={{ boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)' }}>
               <Col
                  className={styles.cart_contact_col}
                  xs={{ order: 2, span: 24 }}
                  sm={{ order: 2, span: 24 }}
                  md={{ order: 2, span: 24 }}
                  lg={{ order: 1, span: 12 }}
                  xl={{ order: 1, span: 12 }}
                  xxl={{ order: 1, span: 12 }}>
                  <div className={styles.cart_logo}>
                     <img src={glLettersLogo} width={70} height={70} alt='gl-logo' />
                  </div>
                  {discount && (
                     <ShoppingForm
                        address={user.address}
                        onSubmited={handleCheckout}
                        minimalAmount={
                           walletType === 'Product' ? true : total + TRAVEL_EXPENSES >= minimalAmount

                           /**
 *                            payForm === 1
                              ? discount !== undefined && discount.amount
                                 ? total +
                                      TRAVEL_EXPENSES -
                                      (discount.percentage ? getAmountDiscount(total, discount.amount || 0) : discount.amount) >=
                                   minimalAmount
                                 : total + TRAVEL_EXPENSES >= minimalAmount
                              : discount !== undefined && discount.amount
                              ? getTotalDeferred(payForm, total + TRAVEL_EXPENSES) -
                                   (discount.percentage ? getAmountDiscount(total, discount.amount || 0) : discount.amount) >=
                                minimalAmount
                              : getTotalDeferred(payForm, total + TRAVEL_EXPENSES) >= minimalAmount
 */
                        }
                     />
                  )}
               </Col>
               <Col
                  className={styles.cart_products_col}
                  style={{ backgroundColor: '#ECECEC' }}
                  xs={{ order: 1, span: 24 }}
                  sm={{ order: 1, span: 24 }}
                  md={{ order: 1, span: 24 }}
                  lg={{ order: 2, span: 12 }}
                  xl={{ order: 2, span: 12 }}
                  xxl={{ order: 2, span: 12 }}>
                  {products.length > 0 && (
                     <>
                        {products.map((i: CartProductI, index: number) => (
                           <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
                              <div style={{ width: '20%' }}>
                                 <img style={{ borderRadius: 5, objectFit: 'cover' }} width={70} height={70} alt='service-item' src={i.uri} />
                              </div>
                              <div style={{ width: '35%', paddingLeft: 5, display: 'flex', justifyContent: 'center' }}>
                                 <h4 style={{ margin: 0 }}>{i.nameProduct[lang]}</h4>
                              </div>
                              {!i.gift && (
                                 <div style={{ width: '25%', textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
                                    <div style={{ border: '1px solid #D4D4D4', padding: '5px 0' }}>
                                       <Button
                                          style={{ width: 25, color: '#000' }}
                                          size='small'
                                          type='link'
                                          onClick={() => {
                                             dispatch(
                                                deleteProdCart({
                                                   id: i.id,
                                                })
                                             );
                                          }}>
                                          -
                                       </Button>
                                       <Button size='small' value='default' disabled type='link'>
                                          {i.numberProducts}
                                       </Button>
                                       <Button
                                          style={{ width: 25, color: '#000' }}
                                          size='small'
                                          type='link'
                                          onClick={() => {
                                             dispatch(
                                                setProdCart({
                                                   id: i.id,
                                                   nameProduct: i.nameProduct,
                                                   numberProducts: 1,
                                                   cost: i.cost,
                                                   uri: i.uri,
                                                })
                                             );
                                          }}>
                                          +
                                       </Button>
                                    </div>
                                 </div>
                              )}

                              <div style={{ width: '20%', display: 'flex', justifyContent: 'flex-end' }}>
                                 {i.gift ? (
                                    <h4 style={{ color: '#888' }}>
                                       <span style={{ color: '#888', textDecoration: 'line-through', fontSize: '0.9em' }}>
                                          {formatter(locale).format(i.cost).replace('MX', '')}
                                       </span>
                                    </h4>
                                 ) : (
                                    <h4 style={{ margin: 0 }}>{formatter(locale).format(i.cost).replace('MX', '')}</h4>
                                 )}
                              </div>
                           </div>
                        ))}
                        <br />
                        <hr />
                     </>
                  )}
                  <>
                     <br />
                     <div
                        className={styles.shopping_radio_div}
                        style={{ display: 'flex', width: '100%', marginTop: 3 }}
                        onClick={() => {
                           handlePays(1);
                        }}>
                        <div style={{ minWidth: width >= 350 ? '60%' : '50%' }}>
                           <h3>{bagI18n[lang].travelExpenseText}</h3>
                        </div>
                        <div style={{ minWidth: width >= 350 ? '40%' : '50%', textAlign: 'right' }}>
                           {formatter(locale).format(TRAVEL_EXPENSES).replace('MX', '')}
                        </div>
                     </div>
                  </>
                  <hr />
                  <br />
                  <div
                     className={styles.shopping_radio_div}
                     style={{ display: 'flex', width: '100%', marginTop: 3 }}
                     onClick={() => {
                        handlePays(1);
                     }}>
                     <div style={{ minWidth: width >= 350 ? '60%' : '50%' }}>
                        <h3>{bagI18n[lang].legendToBuy}</h3>
                     </div>
                     <div style={{ minWidth: width >= 350 ? '40%' : '50%', textAlign: 'right' }}>
                        {formatter(locale).format(minimalAmount).replace('MX', '')}
                     </div>
                  </div>
                  <hr />
                  <br />
                  <h3>{bagI18n[lang].discountCode}</h3>
                  <Row gutter={[4, 4]}>
                     <Col xs={18} sm={18} md={20} lg={18} xl={20} xxl={20}>
                        <Input
                           size='large'
                           // disabled={code ? code.length > 0 : !(Object.values(gift).length > 0 || discount)}
                           placeholder={bagI18n[lang].discountPlaceholder}
                           value={code}
                           onChange={(e) => setCode(e.target.value ? e.target.value.toUpperCase().trim() : '')}
                           maxLength={15}
                        />
                     </Col>
                     <Col xs={6} sm={6} md={4} lg={6} xl={4} xxl={4}>
                        <Button
                           type='primary'
                           ghost
                           onClick={
                              user.date !== ''
                                 ? handleValidateCode
                                 : () => {
                                      notification.warning({
                                         message: notifI18n[lang].dateCodeInvalidTitleNotif,
                                         description: notifI18n[lang].dateCodeInvalidDescriptionNotif,
                                      });
                                   }
                           }
                           // disabled={!(Object.values(gift).length > 0 || discount)}
                        >
                           {bagI18n[lang].btnAdd}
                        </Button>
                     </Col>
                  </Row>
                  <br />
                  <hr />
                  <br />
                  <>
                     {discount && discount.amount > 0 && (
                        <>
                           <Row>
                              <Col xs={12} sm={12} md={12} lg={12} xl={12} xxl={12}>
                                 <p>{bagI18n[lang].discountText}</p>
                              </Col>
                              <Col style={{ textAlign: 'right' }} xs={12} sm={12} md={12} lg={12} xl={12} xxl={12}>
                                 {discount.percentage ? <h3>{discount.amount} %</h3> : <h3>-${discount.amount}</h3>}
                              </Col>
                           </Row>
                           <hr />
                        </>
                     )}
                     <h2 style={{ marginTop: 15 }}>{bagI18n[lang].wayToPayText}</h2>
                     <h4 style={{ marginTop: 20 }}>{bagI18n[lang].creditDebitText}</h4>
                     <div style={{ display: 'flex', marginTop: 15, marginBottom: 20 }}>
                        <Radio
                           onChange={() => {
                              handlePays(1);
                           }}
                           checked={payForm === 1}
                        />
                        <div
                           className={styles.shopping_radio_div}
                           style={{ display: 'flex', width: '100%', marginTop: 3 }}
                           onClick={() => {
                              handlePays(1);
                           }}>
                           <div style={{ minWidth: width >= 350 ? '60%' : '50%' }}>{bagI18n[lang].singleExhibition}</div>
                           <div style={{ minWidth: width >= 350 ? '40%' : '50%', textAlign: 'right' }}>
                              {discount !== undefined && discount.amount
                                 ? formatter(locale)
                                      .format(
                                         total +
                                            TRAVEL_EXPENSES -
                                            (discount.percentage ? getAmountDiscount(total, discount.amount || 0) : discount.amount)
                                      )
                                      .replace('MX', '')
                                 : formatter(locale)
                                      .format(total + TRAVEL_EXPENSES)
                                      .replace('MX', '')}
                           </div>
                        </div>
                     </div>
                     {locale === 'es' && (
                        <>
                           <h4 style={{ marginTop: 15 }}>{bagI18n[lang].deferredPays}</h4>
                           {/* {total >= 300 && */}
                           <div style={{ display: 'flex', marginTop: 20 }}>
                              <Radio
                                 onChange={() => {
                                    handlePays(3);
                                 }}
                                 checked={payForm === 3}
                                 disabled={
                                    discount !== undefined && discount.amount
                                       ? getTotalDeferred(
                                            payForm,
                                            total +
                                               TRAVEL_EXPENSES -
                                               (discount.percentage ? getAmountDiscount(total, discount.amount || 0) : discount.amount)
                                         ) < 300
                                       : getTotalDeferred(payForm, total + TRAVEL_EXPENSES) < 300
                                 }
                              />
                              <div
                                 className={styles.shopping_radio_div}
                                 style={{ display: 'flex', width: '100%', marginTop: 3 }}
                                 onClick={() => {
                                    if (discount !== undefined && discount.amount) {
                                       if (
                                          getTotalDeferred(
                                             payForm,
                                             total +
                                                TRAVEL_EXPENSES -
                                                (discount.percentage ? getAmountDiscount(total, discount.amount || 0) : discount.amount)
                                          ) >= 300
                                       )
                                          handlePays(3);
                                    } else {
                                       if (getTotalDeferred(payForm, total + TRAVEL_EXPENSES) >= 300) handlePays(3);
                                    }
                                 }}>
                                 <div style={{ minWidth: width >= 350 ? '60%' : '50%' }}>3 {bagI18n[lang].paysOf}</div>
                                 <div style={{ minWidth: width >= 350 ? '40%' : '50%', textAlign: 'right' }}>
                                    {discount !== undefined && discount.amount
                                       ? formatter(locale)
                                            .format(
                                               (getTotalDeferred(3, total + TRAVEL_EXPENSES) -
                                                  (discount.percentage ? getAmountDiscount(total, discount.amount || 0) : discount.amount)) /
                                                  3
                                            )
                                            .replace('MX', '')
                                       : formatter(locale)
                                            .format(getAmountByMonth(total + TRAVEL_EXPENSES, 0.053, 3))
                                            .replace('MX', '')}
                                 </div>
                              </div>
                           </div>
                           {/* } */}
                           {/* {total >= 600 && <> */}
                           <Divider style={{ margin: '20px 0' }} />
                           <div style={{ display: 'flex' }}>
                              <Radio
                                 onChange={() => {
                                    handlePays(6);
                                 }}
                                 checked={payForm === 6}
                                 disabled={
                                    discount !== undefined && discount.amount
                                       ? getTotalDeferred(
                                            payForm,
                                            total +
                                               TRAVEL_EXPENSES -
                                               (discount.percentage ? getAmountDiscount(total, discount.amount || 0) : discount.amount)
                                         ) < 600
                                       : getTotalDeferred(payForm, total + TRAVEL_EXPENSES) < 600
                                 }
                              />
                              <div
                                 className={styles.shopping_radio_div}
                                 style={{ display: 'flex', width: '100%', marginTop: 3 }}
                                 onClick={() => {
                                    if (discount !== undefined && discount.amount) {
                                       if (
                                          getTotalDeferred(
                                             payForm,
                                             total +
                                                TRAVEL_EXPENSES -
                                                (discount.percentage ? getAmountDiscount(total, discount.amount || 0) : discount.amount)
                                          ) >= 600
                                       )
                                          handlePays(6);
                                    } else {
                                       if (getTotalDeferred(payForm, total + TRAVEL_EXPENSES) >= 600) handlePays(6);
                                    }
                                 }}>
                                 <div style={{ minWidth: width >= 350 ? '60%' : '50%' }}>6 {bagI18n[lang].paysOf}</div>
                                 <div style={{ minWidth: width >= 350 ? '40%' : '50%', textAlign: 'right' }}>
                                    {discount !== undefined && discount.amount
                                       ? formatter(locale)
                                            .format(
                                               (getTotalDeferred(6, total + TRAVEL_EXPENSES) -
                                                  (discount.percentage ? getAmountDiscount(total, discount.amount || 0) : discount.amount)) /
                                                  6
                                            )
                                            .replace('MX', '')
                                       : formatter(locale)
                                            .format(getAmountByMonth(total + TRAVEL_EXPENSES, 0.08, 6))
                                            .replace('MX', '')}
                                 </div>
                              </div>
                           </div>
                           {/* </>} */}
                           {/* {total >= 900 && <> */}
                           <Divider style={{ margin: '20px 0' }} />
                           <div style={{ display: 'flex', marginBottom: 20 }}>
                              <Radio
                                 onChange={() => {
                                    handlePays(9);
                                 }}
                                 checked={payForm === 9}
                                 disabled={
                                    discount !== undefined && discount.amount
                                       ? getTotalDeferred(
                                            payForm,
                                            total +
                                               TRAVEL_EXPENSES -
                                               (discount.percentage ? getAmountDiscount(total, discount.amount || 0) : discount.amount)
                                         ) < 900
                                       : getTotalDeferred(payForm, total + TRAVEL_EXPENSES) < 900
                                 }
                              />
                              <div
                                 className={styles.shopping_radio_div}
                                 style={{ display: 'flex', width: '100%', marginTop: 3 }}
                                 onClick={() => {
                                    if (discount !== undefined && discount.amount) {
                                       if (
                                          getTotalDeferred(
                                             payForm,
                                             total +
                                                TRAVEL_EXPENSES -
                                                (discount.percentage ? getAmountDiscount(total, discount.amount || 0) : discount.amount)
                                          ) >= 900
                                       )
                                          handlePays(9);
                                    } else {
                                       if (getTotalDeferred(payForm, total + TRAVEL_EXPENSES) >= 900) handlePays(9);
                                    }
                                 }}>
                                 <div style={{ minWidth: width >= 350 ? '60%' : '50%' }}>
                                    9 {bagI18n[lang].paysOf}
                                    <br />
                                    <span style={{ fontSize: 11 }}>({bagI18n[lang].paysOfLegend} 900MX)</span>
                                 </div>
                                 <div style={{ minWidth: width >= 350 ? '40%' : '50%', textAlign: 'right' }}>
                                    {discount !== undefined && discount.amount
                                       ? formatter(locale)
                                            .format(
                                               (getTotalDeferred(9, total + TRAVEL_EXPENSES) -
                                                  (discount.percentage ? getAmountDiscount(total, discount.amount || 0) : discount.amount)) /
                                                  9
                                            )
                                            .replace('MX', '')
                                       : formatter(locale)
                                            .format(getAmountByMonth(total + TRAVEL_EXPENSES, 0.11, 9))
                                            .replace('MX', '')}
                                 </div>
                              </div>
                           </div>
                           {/* </>} */}
                           {/* {total >= 1200 && <> */}
                           <Divider style={{ margin: '20px 0' }} />
                           <div style={{ display: 'flex', marginBottom: 20 }}>
                              <Radio
                                 onChange={() => {
                                    handlePays(12);
                                 }}
                                 checked={payForm === 12}
                                 disabled={
                                    discount !== undefined && discount.amount
                                       ? getTotalDeferred(
                                            payForm,
                                            total +
                                               TRAVEL_EXPENSES -
                                               (discount.percentage ? getAmountDiscount(total, discount.amount || 0) : discount.amount)
                                         ) < 1200
                                       : getTotalDeferred(payForm, total + TRAVEL_EXPENSES) < 1200
                                 }
                              />
                              <div
                                 className={styles.shopping_radio_div}
                                 style={{ display: 'flex', width: '100%', marginTop: 3 }}
                                 onClick={() => {
                                    if (discount !== undefined && discount.amount) {
                                       if (
                                          getTotalDeferred(
                                             payForm,
                                             total +
                                                TRAVEL_EXPENSES -
                                                (discount.percentage ? getAmountDiscount(total, discount.amount || 0) : discount.amount)
                                          ) >= 1200
                                       )
                                          handlePays(12);
                                    } else {
                                       if (getTotalDeferred(payForm, total + TRAVEL_EXPENSES) >= 1200) handlePays(12);
                                    }
                                 }}>
                                 <div style={{ minWidth: width >= 350 ? '60%' : '50%' }}>
                                    12 {bagI18n[lang].paysOf}
                                    <br />
                                    <span style={{ fontSize: 11 }}>({bagI18n[lang].paysOfLegend} 1200MX)</span>
                                 </div>
                                 <div style={{ minWidth: width >= 350 ? '40%' : '50%', textAlign: 'right' }}>
                                    {discount !== undefined && discount.amount
                                       ? formatter(locale)
                                            .format(
                                               (getTotalDeferred(12, total + TRAVEL_EXPENSES) -
                                                  (discount.percentage ? getAmountDiscount(total, discount.amount || 0) : discount.amount)) /
                                                  12
                                            )
                                            .replace('MX', '')
                                       : formatter(locale)
                                            .format(getAmountByMonth(total + TRAVEL_EXPENSES, 0.14, 12))
                                            .replace('MX', '')}
                                 </div>
                              </div>
                           </div>
                           {/* </>} */}
                        </>
                     )}
                     <Divider style={{ margin: '20px 0' }} />
                  </>
                  <Row>
                     <Col xs={12} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <h2>Total: </h2>
                     </Col>
                     <Col style={{ textAlign: 'right' }} xs={12} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <h2>
                           {payForm === 1
                              ? discount !== undefined && discount.amount
                                 ? formatter(locale)
                                      .format(
                                         total +
                                            TRAVEL_EXPENSES -
                                            (discount.percentage ? getAmountDiscount(total, discount.amount || 0) : discount.amount)
                                      )
                                      .replace('MX', '')
                                 : formatter(locale)
                                      .format(total + TRAVEL_EXPENSES)
                                      .replace('MX', '')
                              : discount !== undefined && discount.amount
                                ? formatter(locale)
                                     .format(
                                        getTotalDeferred(payForm, total + TRAVEL_EXPENSES) -
                                           (discount.percentage ? getAmountDiscount(total, discount.amount || 0) : discount.amount)
                                     )
                                     .replace('MX', '')
                                : formatter(locale)
                                     .format(getTotalDeferred(payForm, total + TRAVEL_EXPENSES))
                                     .replace('MX', '')}
                        </h2>
                     </Col>
                  </Row>
               </Col>
            </Row>
         </div>
         {showModal && (
            <Modal
               open={showModal}
               footer={null}
               centered
               title={bagI18n[lang].shopModalTitle}
               onCancel={() => {
                  setShowModal(false);
               }}>
               <Elements stripe={stripePromise}>
                  <CheckoutForm code={wallet} months={payForm} sale={sale} showModal={showModal} />
               </Elements>
            </Modal>
         )}
      </div>
   );
};
export default ShoppingInfo;
